import { useCallback, useEffect, useRef, useState } from 'react';
import { getWatchProgress, saveWatchProgress } from '../services/watchProgressService';

/**
 * Resumes a title where the viewer stopped, and keeps saving where they are.
 *
 * ## The trap this is built around
 *
 * A player that saves progress as it goes will, left to itself, save 0:00 in the moments
 * after a title loads - before the resume seek has happened. That overwrites the real
 * position with the start, so the feature destroys its own data on every visit.
 *
 * So nothing is saved until two things are true: the saved position has been fetched
 * and either applied or found not worth resuming, AND the viewer has actually started
 * playback. Opening a page and leaving without pressing play changes nothing.
 *
 * ## When it saves
 *
 * Every 10 seconds of playback, on pause, after a seek, at the end, and when the page is
 * hidden or closed. The page-close save uses keepalive so it survives the unload; without
 * it the last stretch someone watched before closing the tab is lost.
 */

const SAVE_INTERVAL_MS = 10_000;

export interface ResumeState {
  /** Seconds the player resumed from, while the notice should show. Null otherwise. */
  resumedFrom: number | null;
  /** Go back to the beginning, and remember that choice. */
  startOver: () => void;
  dismiss: () => void;
}

export function useWatchProgress(
  getMedia: () => HTMLMediaElement | null,
  contentId: string | null | undefined,
  enabled: boolean,
): ResumeState {
  const [resumedFrom, setResumedFrom] = useState<number | null>(null);

  const resumeAtRef = useRef<number | null>(null);
  const decidedRef = useRef(false);      // fetch done, and resume applied or not needed
  const playedRef = useRef(false);       // the viewer has started playback
  const lastSaveRef = useRef(0);
  const getMediaRef = useRef(getMedia);
  getMediaRef.current = getMedia;

  const canSave = () => enabled && !!contentId && decidedRef.current && playedRef.current;

  const save = useCallback((keepalive = false, positionOverride?: number) => {
    const media = getMediaRef.current();
    if (!media || !contentId || !enabled || !decidedRef.current || !playedRef.current) return;
    const duration = media.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;
    lastSaveRef.current = Date.now();
    const position = positionOverride ?? media.currentTime;
    // Fire and forget. A failed save must never interrupt playback, and the next
    // interval will try again.
    saveWatchProgress(contentId, position, duration, keepalive).catch(() => {});
  }, [contentId, enabled]);

  // Reset for each title, then fetch where the viewer stopped.
  useEffect(() => {
    resumeAtRef.current = null;
    decidedRef.current = false;
    playedRef.current = false;
    lastSaveRef.current = 0;
    setResumedFrom(null);

    if (!enabled || !contentId) {
      decidedRef.current = true;
      return;
    }

    let cancelled = false;
    getWatchProgress(contentId)
      .then((progress) => {
        if (cancelled) return;
        resumeAtRef.current = progress?.resumable ? progress.positionSeconds : null;
        tryApplyResume();
        if (resumeAtRef.current == null) decidedRef.current = true;
      })
      .catch(() => {
        if (!cancelled) decidedRef.current = true;   // cannot fetch: just play from the start
      });

    return () => {
      cancelled = true;
    };
  }, [contentId, enabled]);

  /** Seek once both the position and the media's duration are known - in either order. */
  const tryApplyResume = () => {
    const media = getMediaRef.current();
    const at = resumeAtRef.current;
    if (at == null || !media) return;
    if (media.readyState < 1 || !Number.isFinite(media.duration) || media.duration <= 0) return;
    media.currentTime = Math.min(at, media.duration);
    resumeAtRef.current = null;
    decidedRef.current = true;
    setResumedFrom(at);
  };

  // Media element listeners. Re-attached per title because the element can change.
  useEffect(() => {
    if (!enabled || !contentId) return;
    const media = getMediaRef.current();
    if (!media) return;

    const onMetadata = () => tryApplyResume();
    const onPlay = () => { playedRef.current = true; };
    const onTimeUpdate = () => {
      if (canSave() && !media.paused && Date.now() - lastSaveRef.current >= SAVE_INTERVAL_MS) save();
    };
    const onPause = () => { if (!media.ended) save(); };
    const onSeeked = () => { if (canSave()) save(); };
    const onEnded = () => save(false, media.duration);

    media.addEventListener('loadedmetadata', onMetadata);
    media.addEventListener('play', onPlay);
    media.addEventListener('timeupdate', onTimeUpdate);
    media.addEventListener('pause', onPause);
    media.addEventListener('seeked', onSeeked);
    media.addEventListener('ended', onEnded);
    // Metadata may already have loaded before the position arrived.
    tryApplyResume();

    const onHide = () => { if (document.visibilityState === 'hidden') save(true); };
    const onPageHide = () => save(true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      // Leaving the title - navigating away inside the app - counts as stopping.
      save(true);
      media.removeEventListener('loadedmetadata', onMetadata);
      media.removeEventListener('play', onPlay);
      media.removeEventListener('timeupdate', onTimeUpdate);
      media.removeEventListener('pause', onPause);
      media.removeEventListener('seeked', onSeeked);
      media.removeEventListener('ended', onEnded);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, enabled, save]);

  const startOver = useCallback(() => {
    const media = getMediaRef.current();
    if (media) media.currentTime = 0;
    setResumedFrom(null);
    // Record the choice, so the next visit does not resume them straight back.
    playedRef.current = true;
    decidedRef.current = true;
    save(false, 0);
  }, [save]);

  const dismiss = useCallback(() => setResumedFrom(null), []);

  return { resumedFrom, startOver, dismiss };
}
